import React, { useEffect, useRef } from 'react';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'services/applicationInsightsService';
import { useLocation, useParams } from 'react-router';
import { useFetch } from 'hooks';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import { useTerminologyConfig, useUserSettings } from 'context/DataContext';
import dayjs from 'dayjs';

import { AppLayout } from 'layouts';
import { Loading, Link, Button, SkeletonText } from 'components/atoms';
import { Redirect } from 'react-router-dom';
import { GatewayPermissions } from 'auth/constants/permissions';
import { OnlineScreeningResponse } from 'types';
import { Breadcrumbs } from 'components/molecules';
import { BreadcrumbItem } from 'components/molecules/breadcrumbs/types';
import { ThirdPartyRouteInfo } from 'types/router';
import { ScheduledDeletionDate } from './Report.styles';

type ReportURLParams = {
  id: string;
};

function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

const ReportHtml = () => {
  const { tokenInfo } = useAuth();
  const { hasPermission } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi } = useConfig();
  const location = useLocation<{
    mainThirdParty: ThirdPartyRouteInfo;
    linkedThirdParty: ThirdPartyRouteInfo;
  }>();
  const locationState = location.state;
  const reportContainer = useRef<HTMLDivElement>(null);
  const { id } = useParams<ReportURLParams>();

  const {
    data,
    loading,
    error,
    responseStatus: responseHtmlStatus,
  } = useFetch(
    `${vantageWebApi}/onlinereport/${id}/html`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    },
    'text'
  );

  const { data: statusData, loading: statusLoading } = useFetch<OnlineScreeningResponse>(
    `${vantageWebApi}/onlinescreening/${id}/status`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  useEffect(() => {
    if (!data || isJsonString(data) || responseHtmlStatus == 404) {
      return;
    }

    // Place the report markup inside a shadow root to encapsulate style.
    const shadowRoot = reportContainer.current.attachShadow({ mode: 'open' });
    shadowRoot.innerHTML = data;
    // eslint-disable-next-line
  }, [data]);

  if (responseHtmlStatus == 404) {
    return <Redirect to="/not-found" />;
  }

  if (responseHtmlStatus === 401) {
    return <Redirect to="/unauthorised" />;
  }

  const getBreadcrumb = (): BreadcrumbItem[] => {
    if (locationState?.linkedThirdParty) {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: locationState.mainThirdParty.name,
          route: `/third-party/profile/${locationState.mainThirdParty.id}`,
        },
        {
          label: locationState.linkedThirdParty.name,
          route: {
            pathname: `/third-party/profile/${locationState.linkedThirdParty.id}`,
            state: locationState,
          },
        },
        {
          label: 'Screening Results',
          route: {
            pathname: `/third-party/screening/${id}`,
            state: locationState,
          },
        },
        {
          label: `Screening Report`,
        },
      ] as BreadcrumbItem[];
    } else {
      return [
        {
          label: terminologyConfig?.thirdPartySingularNaming + ' manager',
          route: '/',
        },
        {
          label: statusData?.thirdParty.name,
          route: `/third-party/profile/${statusData?.thirdParty.id}`,
        },
        {
          label: 'Screening Results',
          route: `/third-party/screening/${id}`,
        },
        {
          label: `Screening Report`,
        },
      ] as BreadcrumbItem[];
    }
  };

  const expiryDate = dayjs(statusData?.expiryDate, 'YYYY-MM-DD[T]00:00:00[Z]').format('DD/MM/YYYY');

  return (
    <AppLayout
      topBarSlot={statusLoading ? <SkeletonText /> : <Breadcrumbs items={getBreadcrumb()} />}
    >
      {statusData?.expiryDate && (
        <ScheduledDeletionDate>
          <strong>Scheduled Deletion Date: </strong>
          {expiryDate}
        </ScheduledDeletionDate>
      )}
      {loading || statusLoading ? (
        <Loading />
      ) : error || (responseHtmlStatus && responseHtmlStatus !== 200) ? (
        <p>Error loading report</p>
      ) : (
        <>
          {hasPermission(GatewayPermissions.ApproveRejectThirdParty) &&
            statusData?.client.isUpdateApprovalStatusModalEnabled &&
            !statusData?.thirdParty.hasApprovals && (
              <div style={{ position: 'relative', alignContent: 'flex-end' }}>
                <Button
                  key="viewReport"
                  style={{ position: 'absolute', right: 0, zIndex: 1 }}
                  secondary
                >
                  <Link
                    to={{
                      pathname: `/third-party/edit-approval/${statusData?.thirdParty.id}`,
                    }}
                  >
                    Update approval status
                  </Link>
                </Button>
              </div>
            )}
          <div ref={reportContainer}></div>
        </>
      )}
    </AppLayout>
  );
};

export default withAITracking(reactPlugin, ReportHtml);
